import cx from "classnames";

import { Button } from "atoms/button/Button";
import Affirm from "atoms/icon/icons/affirm.svg";
import RightIcon from "atoms/icon/icons/chevron_right_small.svg";
import Close from "atoms/icon/icons/close.svg";
import Cart from "atoms/icon/icons/ic_affirm_Step-1_ol.svg";
import Calendar from "atoms/icon/icons/ic_affirm_Step-3_ol.svg";
import Star from "atoms/icon/icons/ic_affirm_Step-4_ol.svg";
import { IconButton } from "atoms/icon-button/IconButton";
import { TextButton } from "atoms/text-button/TextButton";

export interface AffirmModalProps {
  platformLogo: string;
  /** Open/hide */
  open?: boolean;
  /** On close callback */
  onClose?: () => void;

  /** On shop now callback, if provided, will show the shop now button,
   * if undefined or null, it won't show the shop now button.
   */
  onShopNow?: (() => void) | null;

  /**
   * On book appointment callback, if provided, will show the book appointment button,
   * if undefined or null, it won't show the book appointment button.
   */
  onBookAppointment?: (() => void) | null;

  /** To hide reward text */
  hideRewardText?: boolean;
}

const COMMON_STEPS_2_3 = [
  {
    description: "Select Affirm at checkout",
    Icon: <Affirm className="mb-0.5 mt-1 h-7" width={55} />,
  },
  {
    description: "Get approved and choose the payment plan that suits you",
    Icon: <Calendar className="h-8 w-8" />,
  },
];

const LAST_STEP = {
  description: "Checkout now to treat today, pay later.",
  Icon: <Star className="h-8 w-8 fill-brand-color" />,
};

const FIRST_STEP_SHOP_ENABLED = {
  description: "Choose your favorite services in ‘Shop’",
  Icon: <Cart className="h-8 w-8 fill-brand-color" />,
};

const FIRST_STEP_NON_SHOP_ENABLED = {
  description: "Your provider creates a treatment plan just for you",
  Icon: <Cart className="h-8 w-8 fill-brand-color" />,
};

export const AffirmStep = ({
  description,
  Icon,
  stepNumber = 1,
}: {
  description: string;
  Icon: React.ReactNode;
  stepNumber?: number;
}) => (
  <div className="mt-4 h-[134px] w-[144px] text-center">
    <div className="my-4 flex h-16 flex-col items-center gap-2">
      {Icon}
      <p className="font-serif text-h3 text-primary"> Step {stepNumber}</p>
    </div>
    <div className="w-[140px] text-body2 text-primary">{description}</div>
  </div>
);

const CloseSvg = () => <Close className="h-5 w-5 text-center" />;
const AffirmSteps = ({
  steps,
}: {
  steps: {
    description: string;
    Icon: JSX.Element;
  }[];
}) => (
  <div className="mb-px mt-1 flex flex-wrap items-center justify-center gap-4 gap-y-2">
    {steps.map((step, index: number) => (
      <AffirmStep
        stepNumber={index + 1}
        description={step.description}
        Icon={step.Icon}
        key={index}
      />
    ))}
  </div>
);

export const AffirmModal = ({
  platformLogo,
  open,
  onClose,
  onShopNow,
  onBookAppointment,
  hideRewardText = false,
}: AffirmModalProps) => {
  if (!open) {
    return null;
  }

  const LastStepWithRewardsFactoredIn = {
    ...LAST_STEP,
    description: hideRewardText
      ? LAST_STEP.description
      : `${LAST_STEP.description} Earn rewards.`,
  };

  const StepContent = onBookAppointment
    ? [
        FIRST_STEP_NON_SHOP_ENABLED,
        ...COMMON_STEPS_2_3,
        LastStepWithRewardsFactoredIn,
      ]
    : [
        FIRST_STEP_SHOP_ENABLED,
        ...COMMON_STEPS_2_3,
        LastStepWithRewardsFactoredIn,
      ];

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-black/[35%]"
        onClick={onClose}
        data-testid="div-dismiss-modal"
      />
      <div
        className={cx(
          "fixed bottom-0 left-0 right-0 top-0 z-40 mx-auto my-auto flex w-full flex-col",
          "items-center justify-center"
        )}
      >
        <div
          className={cx(
            "rounded-lg",
            onShopNow || onBookAppointment ? "h-[575px]" : "h-[526px]",
            "bg-light-grey",
            "w-[374px] bg-[url('assets/images/affirmModalBG.jpeg')] bg-cover bg-center"
          )}
        >
          <div className="pt-0.5">
            <IconButton
              svg={CloseSvg}
              size="small"
              style="iconOnly"
              onClick={onClose}
            />
          </div>
          <div className="mb-4 flex items-end justify-center gap-x-2.5 pt-2.5">
            <Affirm width={98} className="h-12" />
            <p className="mb-px pb-0.5 text-body1 text-primary">with</p>
            <img
              src={platformLogo}
              alt="logo"
              className="max-width-[140px] h-[38px] pb-0.5"
            />
          </div>
          <div className="flex justify-center font-serif text-h3 text-primary">
            Treat today. Pay later. {!hideRewardText && "Earn rewards."}
          </div>
          <AffirmSteps steps={StepContent} />
          {onShopNow && (
            <div className="mt-10 flex justify-center text-body2 text-primary">
              <TextButton onClick={onShopNow}>
                Shop now <RightIcon className="h-6 w-6 fill-brand-color" />
              </TextButton>
            </div>
          )}
          {onBookAppointment && (
            <div className="mt-10 flex justify-center text-body2 text-primary">
              <Button style="primary" onClick={onBookAppointment}>
                Book your appointment
              </Button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
